import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Plot from 'react-plotly.js';

import dayjs from 'dayjs';
import { motion, useAnimationControls, useReducedMotion } from 'framer-motion';
import Plotly from 'plotly.js/dist/plotly';

import { useHorizontalWheelPan } from '../composables/useHorizontalWheelPan';
import { CONSTANTS, dataUtils } from '../composables/utils';

/**
 * Temperature trend chart.
 *
 * Panning is Plotly's own `dragmode: 'pan'` rather than a hand-rolled gesture.
 * That is what makes the chart track the finger 1:1 and read as one continuous
 * trace being scrolled, instead of discrete windows being swapped: the parent
 * keeps a span of data loaded either side of what's visible, so there is always
 * real chart to move onto.
 *
 * Only the x-axis moves. The y-axis is locked so a horizontal drag can't
 * rescale temperature out from under the reader, and its range is derived from
 * the whole loaded series so it stays put while panning.
 */
function TemperatureGraph({ detail, record, xRange, minTime = null, onPanEnd }: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime' });
    const { normalData, abnormalData, allData } = dataUtils.processTemperatureData(detail, record);

    const allPlotData = dataUtils.extractPlotData(allData);
    const normalPlotData = dataUtils.extractPlotData(normalData);
    const abnormalPlotData = dataUtils.extractPlotData(abnormalData);

    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    // Plotly's DOM node, so the wheel gesture can move the axis directly.
    const graphRef = useRef<any>(null);
    // Plotly.relayout emits plotly_relayout, so without this the wheel handler
    // would commit a range — and trigger a fetch — on every momentum frame.
    const wheelPanning = useRef(false);
    // Set while the floor clamp is writing the axis back, for the same reason.
    const correcting = useRef(false);

    const bounceControls = useAnimationControls();
    const prefersReducedMotion = useReducedMotion();

    // The floor changes as history is discovered, and the wheel listener is
    // attached once, so it reads the current value through a ref.
    const minTimeRef = useRef(minTime);
    minTimeRef.current = minTime;

    /** Nudge the chart to show a pan was refused. Positive = rightward. */
    const bounce = (sign: number) => {
        if (prefersReducedMotion) return;
        bounceControls.start({
            x: [0, 20 * sign, 0],
            transition: { duration: 0.32, ease: 'easeOut' },
        });
    };

    useHorizontalWheelPan({
        graphRef,
        containerRef,
        minTimeRef,
        onBlocked: () => bounce(1),
        onGestureChange: (isActive) => {
            wheelPanning.current = isActive;
        },
        onSettled: (from, to) => onPanEnd?.(from, to),
    });

    // Calculate y-axis range with minimum difference of 20
    const getYAxisRange = () => {
        const yValues = allPlotData.y;
        if (!yValues || yValues.length === 0) return [0, 20];

        const dataMin = Math.min(...yValues);
        const dataMax = Math.max(...yValues);
        const dataRange = dataMax - dataMin;

        // Ensure minimum range of 20
        if (dataRange < 20) {
            const center = (dataMin + dataMax) / 2;
            return [center - 10, center + 10];
        }

        // Add 5% padding on each side for better visualization
        const padding = dataRange * 0.05;
        return [dataMin - padding, dataMax + padding];
    };

    useEffect(() => {
        const node = containerRef.current;
        if (!node) {
            return undefined;
        }

        const updateSize = () => {
            const { width, height } = node.getBoundingClientRect();
            setDimensions({
                width,
                height,
            });
        };

        updateSize();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', updateSize);
            return () => window.removeEventListener('resize', updateSize);
        }

        const observer = new ResizeObserver(updateSize);
        observer.observe(node);

        return () => observer.disconnect();
    }, []);

    const plotData = [
        {
            x: allPlotData.x,
            y: allPlotData.y,
            type: 'scatter',
            mode: 'lines',
            marker: { color: '#E6E6FF' },
            showlegend: false,
        },
        {
            x: normalPlotData.x,
            y: normalPlotData.y,
            type: 'scatter',
            mode: 'markers',
            marker: { color: CONSTANTS.COLORS.NORMAL },
            name: t('normal'),
        },
        {
            x: abnormalPlotData.x,
            y: abnormalPlotData.y,
            type: 'scatter',
            mode: 'markers',
            marker: { color: CONSTANTS.COLORS.ABNORMAL },
            name: t('abnormal'),
        },
    ];

    const isMobile = dimensions.width <= 480;
    // Width follows the container (was a fixed 700px on desktop) so the chart
    // scales with the modal, which now caps its width by viewport height.
    const chartWidth = Math.max(dimensions.width, 0);
    const fallbackHeight = isMobile ? chartWidth * 1.1 : 280;
    const chartHeight = dimensions.height > 0 ? dimensions.height : fallbackHeight;

    // Plotly reports the axis it settled on once the drag finishes. Reading it
    // here (rather than tracking pointer deltas) keeps the committed window
    // exactly equal to what the user is looking at.
    const handleRelayout = (event: any) => {
        // Mid-wheel relayouts are our own; that gesture commits once on settle.
        if (wheelPanning.current) return;
        // So is the corrective one below — reacting to it would loop.
        if (correcting.current) return;

        const from = event?.['xaxis.range[0]'];
        const to = event?.['xaxis.range[1]'];
        if (from === undefined || to === undefined) return;

        // Plotly's drag is free-running, so a drag past the start of the device's
        // history is caught here and pulled back — the rubber-band that tells the
        // user there is nothing older to see.
        const floor = minTimeRef.current;
        if (floor) {
            const start = dayjs(from);
            const end = dayjs(to);
            if (start.isValid() && end.isValid() && start.isBefore(floor)) {
                const width = end.diff(start);
                const clampedFrom = dayjs(floor);
                const clampedTo = clampedFrom.add(width, 'millisecond');
                const formatted = [clampedFrom.format('YYYY-MM-DD HH:mm:ss'), clampedTo.format('YYYY-MM-DD HH:mm:ss')];

                // Push the axis back explicitly. Re-committing the same clamped
                // range is correctly ignored as a no-op, so nothing would re-render
                // and Plotly would sit wherever the drag left it — the chart and
                // the pickers would then disagree.
                correcting.current = true;
                Promise.resolve(Plotly.relayout(graphRef.current, { 'xaxis.range': formatted })).finally(() => {
                    correcting.current = false;
                });

                bounce(1);
                onPanEnd?.(formatted[0], formatted[1]);
                return;
            }
        }

        // Now-clamp: mirror of the floor path at the upper bound. A left drag
        // travels forward and can push end past "now"; the parent's handlePanEnd
        // clamps React state, but Plotly's internal range stays at the drag
        // position because a state-driven re-render doesn't reliably push a new
        // range into Plotly after the user has interacted with it. Write it back
        // here, same shape as the floor clamp.
        //
        // Threshold (>60s) rather than a raw isAfter check: Plotly's date axis
        // pads its rendered range a few seconds past the value we pass in, so a
        // bare isAfter would fire on that tiny numerical noise every time layout
        // is applied — including when the picker triggers a re-render — and set
        // rangeChangeIsPan on the parent, which then makes ensureCoverage skip
        // the picker's expected fetch. Real drags overshoot by minutes to hours,
        // well past this floor.
        const now = dayjs();
        const start = dayjs(from);
        const end = dayjs(to);
        if (start.isValid() && end.isValid() && end.diff(now) > 60_000) {
            const width = end.diff(start);
            const clampedTo = now;
            const clampedFrom = clampedTo.subtract(width, 'millisecond');
            const formatted = [clampedFrom.format('YYYY-MM-DD HH:mm:ss'), clampedTo.format('YYYY-MM-DD HH:mm:ss')];

            correcting.current = true;
            Promise.resolve(Plotly.relayout(graphRef.current, { 'xaxis.range': formatted })).finally(() => {
                correcting.current = false;
            });

            onPanEnd?.(formatted[0], formatted[1]);
            return;
        }

        onPanEnd?.(from, to);
    };

    return (
        <motion.div
            ref={containerRef}
            animate={bounceControls}
            // Override Plotly's default ew-resize cursor on the pan layer — that reads
            // as "resize this axis" instead of "drag the chart." grab / grabbing is
            // the universal drag affordance (maps, image viewers).
            className='h-full w-full [&_.nsewdrag]:!cursor-grab [&_.nsewdrag:active]:!cursor-grabbing'
        >
            {chartWidth > 0 && chartHeight > 0 && (
                <Plot
                    data={plotData}
                    onRelayout={handleRelayout}
                    onInitialized={(_figure: any, graphDiv: any) => {
                        graphRef.current = graphDiv;
                    }}
                    onUpdate={(_figure: any, graphDiv: any) => {
                        graphRef.current = graphDiv;
                    }}
                    layout={{
                        autosize: false,
                        width: chartWidth,
                        height: chartHeight,
                        margin: { l: 48, r: 16, t: 24, b: 48 },
                        dragmode: 'pan',
                        yaxis: {
                            fixedrange: true,
                            dtick: 5, // Set y-axis interval to 5 degrees
                            tickmode: 'linear',
                            showgrid: true,
                            gridcolor: '#E2E8F0',
                            zeroline: true,
                            zerolinecolor: '#94A3B8',
                            tickfont: { size: isMobile ? 10 : 12, color: '#475569' },
                            range: getYAxisRange(),
                        },
                        xaxis: {
                            // Pinned to a date axis. Left to infer, Plotly falls
                            // back to a numeric axis whenever the window holds no
                            // points, and then reports pan results as bare numbers
                            // that parse into nonsense datetimes.
                            type: 'date',
                            // Unlocked so the drag can move it; the parent clamps
                            // the committed result so it can never end past "now".
                            fixedrange: false,
                            range: xRange,
                            showgrid: true,
                            gridcolor: '#E2E8F0',
                            tickfont: { size: isMobile ? 10 : 12, color: '#475569' },
                            automargin: true,
                        },
                        legend: {
                            orientation: 'h',
                            x: 1,
                            y: 1,
                            xanchor: 'right',
                            yanchor: 'top',
                            font: { size: 11, color: '#334155' },
                            bgcolor: 'rgba(255,255,255,0.7)',
                            bordercolor: 'rgba(148,163,184,0.2)',
                            borderwidth: 0,
                        },
                    }}
                    config={{
                        displayModeBar: false,
                        responsive: true,
                        // Wheel-zoom and double-click-autoscale would both change
                        // the window without going through the range state, so the
                        // pickers would silently disagree with the chart.
                        scrollZoom: false,
                        doubleClick: false,
                    }}
                    style={{
                        width: '100%',
                        height: `${chartHeight}px`,
                    }}
                />
            )}
        </motion.div>
    );
}

export default TemperatureGraph;
